import { useLocation } from '@gatsbyjs/reach-router';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import ContentLoader from '@components/ContentLoader/index';
import Icon from '@components/Icon/Icon';
import RoundedButton from '@components/RoundedButton/index';

import { useDeleteNote, usePostNote } from '@hooks/api/extension';
import { useSession } from '@hooks/useSession';

import type { Annotation } from 'generated/types';

import NewNoteForm from './NewNoteForm';
import { Note, NotesWrapper, Remove } from './YourNotes.style';

const messages = defineMessages({
  title: {
    id: 'yourNotes.title',
    defaultMessage: '{role} Notes',
  },
  newNote: {
    id: 'yourNotes.newNote',
    defaultMessage: 'New note',
  },
  postSuccess: {
    id: 'yourNotes.postSuccess',
    defaultMessage: 'Note added',
  },
  deleteNote: {
    id: 'yourNotes.deleteNote',
    defaultMessage: 'Are you sure you want to delete this note?',
  },
  deleteSuccess: {
    id: 'yourNotes.deleteSuccess',
    defaultMessage: 'Note deleted',
  },
  postError: {
    id: 'yourNotes.postError',
    defaultMessage: 'Error adding note',
  },
  deleteError: {
    id: 'yourNotes.deleteError',
    defaultMessage: 'Error deleting note',
  },
  your: {
    id: 'yourNotes.your',
    defaultMessage: 'Your',
  },
  patient: {
    id: 'yourNotes.patient',
    defaultMessage: 'Patient',
  },
});

const ROUTE_RESOURCE_MAP: Record<string, string> = {
  allergies: 'AllergyIntolerance',
  appointments: 'Appointment',
  'emergency-visits': 'Encounter',
  'family-history': 'FamilyMemberHistory',
  'genetic-information': 'Observation',
  hospitalizations: 'Encounter',
  'imaging-tests': 'DiagnosticReport',
  immunizations: 'Immunization',
  'lab-results': 'DiagnosticReport',
  'medical-conditions': 'Condition',
  medications: 'MedicationStatement',
  surgeries: 'Procedure',
  'vital-signs': 'Observation',
};

const getResource = (path: string) => {
  const regex =
    // eslint-disable-next-line prefer-named-capture-group
    /(?<subdir>records)\/(?<recordType>(\w|-)+)\/(?<recordId>[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12})/giu;
  path = path.startsWith('/') ? path.slice(1) : path;
  const groups = regex.exec(path)?.groups;
  if (groups?.recordType && groups.recordType in ROUTE_RESOURCE_MAP) {
    const resourceName = ROUTE_RESOURCE_MAP[groups.recordType];
    const resourceId = groups.recordId;
    return `${resourceName}/${resourceId}`;
  }
  return null;
};

export default function YourNotes({
  notes = [],
}: {
  notes: Annotation[] | undefined;
}) {
  const {
    state: { isClient },
  } = useSession();
  const location = useLocation();
  const [showAdd, setShowAdd] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [postNote, postNoteLoading] = usePostNote();
  const [deleteNote, deleteNoteLoading] = useDeleteNote();
  const [localNotes, setNotes] = useState<Annotation[]>(notes);

  const { formatMessage } = useIntl();

  const loading = postNoteLoading || deleteNoteLoading;

  React.useEffect(() => {
    if (loading) {
      return;
    }
    const resource = getResource(location.pathname);
    setEnabled(Boolean(resource));
  }, [loading, location.pathname]);

  const handleSubmit = async (noteText: string) => {
    const resource = getResource(location.pathname);
    if (!resource) return;
    const response = await postNote({
      input: {
        resource,
        text: noteText,
      },
    });
    const newNotes = response.data?.postNote.extension
      ?.filter((ext) => ext.valueAnnotation)
      .map((ext) => ext.valueAnnotation) as Annotation[];
    setNotes(newNotes);
  };

  const handleDelete = async (note: Annotation) => {
    const resource = getResource(location.pathname);
    if (!note.id || !resource) {
      return;
    }
    const response = await deleteNote({
      input: {
        resource,
        note_id: note.id,
      },
    });
    const newNotes = response.data?.deleteNote.extension
      ?.filter((ext) => ext.valueAnnotation)
      .map((ext) => ext.valueAnnotation) as Annotation[];
    setNotes(newNotes);
  };

  return enabled ? (
    <NotesWrapper>
      <h2>
        {formatMessage(messages.title, {
          role: formatMessage(isClient ? messages.your : messages.patient),
        })}
      </h2>
      <ContentLoader loading={loading}>
        {localNotes
          .sort((a, b) => {
            if (!a.time && !b.time) {
              return 0;
            }
            if (!a.time) {
              return -1;
            }
            if (!b.time) {
              return 1;
            }
            return new Date(a.time).getTime() - new Date(b.time).getTime();
          })
          .map((note, index) => (
            <Note key={`your-notes-${index}`}>
              {isClient ? (
                <ConfirmationDialog
                  onConfirm={async () => handleDelete(note)}
                  title={formatMessage(messages.deleteNote)}
                >
                  {({ handleOpen }) => (
                    <Remove onClick={handleOpen}>
                      <Icon
                        clickable
                        color="text.secondary"
                        name="xmark"
                        type="fas"
                      />
                    </Remove>
                  )}
                </ConfirmationDialog>
              ) : null}
              <p>{note.text}</p>
            </Note>
          ))}
        {isClient ? (
          <Note style={{ textAlign: 'center' }}>
            {showAdd ? (
              <NewNoteForm onSubmit={handleSubmit} />
            ) : (
              <RoundedButton
                autoSize
                iconName="plus"
                onClick={() => setShowAdd(true)}
                variant="white"
              >
                {formatMessage(messages.newNote)}
              </RoundedButton>
            )}
          </Note>
        ) : null}
      </ContentLoader>
    </NotesWrapper>
  ) : null;
}
